import { Link } from 'react-router-dom';
import type { IdeaListItem } from '@shared/types';
import { IDEA_STATUS_LABELS } from '@shared/ideaStatus';
import { formatDate, inlineText, isOverdue, relativeTime, truncate } from '@shared/format';
import { Card } from '../../../components/Card';
import { AiStateBadge, IdeaStatusBadge, TagList } from './ideaBadges';

/**
 * 看板与「我的」列表里的创意卡片。
 *
 * 卡片上只放「决定要不要点进去」的信息：状态、一句话说明、AI 状态、互动计数。
 * 判断内容在详情页，卡片堆太多信息等于没有信息。
 */
export function IdeaCard({
  idea,
  showStatus = false,
}: {
  idea: IdeaListItem;
  /** 看板按列分组时状态已在列头，卡片里就不重复显示 */
  showStatus?: boolean;
}) {
  const overdue = isOverdue(idea.dueAt) && idea.status === 'incubating';

  return (
    <Link to={`/ideas/${idea.id}`} className="block">
      <Card interactive className="h-full">
        <div className="flex flex-col gap-2 p-3">
          <div className="flex items-start justify-between gap-2">
            <h3 className="min-w-0 text-sm font-semibold text-fg">
              {idea.viewer.unread ? <span aria-label="有新动态" className="mr-1 inline-block size-1.5 rounded-full bg-accent align-middle" /> : null}
              <span className="align-middle">{idea.title}</span>
            </h3>
            {showStatus ? <IdeaStatusBadge status={idea.status} className="shrink-0" /> : null}
          </div>

          {idea.summary ? (
            <p className="text-xs leading-relaxed text-fg-muted">{truncate(inlineText(idea.summary), 90)}</p>
          ) : (
            <p className="text-xs text-fg-subtle">（草稿，尚无说明）</p>
          )}

          <TagList tags={idea.tags} />

          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-fg-subtle">
            <span>{idea.authorName}</span>
            <span aria-hidden>·</span>
            <span title={idea.updatedAt}>{relativeTime(idea.updatedAt)}</span>
            {idea.assigneeName ? (
              <>
                <span aria-hidden>·</span>
                <span className={overdue ? 'text-critical' : undefined}>
                  负责人 {idea.assigneeName}
                  {idea.dueAt ? ` · ${formatDate(idea.dueAt)}${overdue ? ' 已逾期' : ''}` : ''}
                </span>
              </>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-fg-muted">
            <AiStateBadge state={idea.aiState} />
            <span title="点赞数">赞 {idea.likeCount}</span>
            <span title="评论数">评 {idea.commentCount}</span>
            {idea.status === 'validating' ? (
              <span className="text-accent">{IDEA_STATUS_LABELS.validating}</span>
            ) : null}
          </div>
        </div>
      </Card>
    </Link>
  );
}
